import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSector } from '../../contexts/SectorContext';
import {
  fetchCentralAnalysis,
  fetchCentralOverview,
  fetchCentralTopics,
  fetchTodayV2,
  proposeFollowUps,
  CentralAnalysis,
  CentralOverview,
  CentralRecommendation,
  CentralTopics,
} from '../services/MendsVisionService';
import { openCentralQuestion } from '../lib/centralQuestion';
import { CentralTopicsPanel } from '../components/central/CentralTopicsPanel';
import { apiErrorMessage } from '../../utils/apiErrorMessage';
import ApprovalsPage from './ApprovalsPage';
import OpportunitiesPage from './OpportunitiesPage';
import { CENTRAL_PERIODS, parsePeriod } from '../lib/centralFormat';
import { runCentralPlan, runFollowUpBatch, type PlanState } from '../lib/centralActions';
import {
  BatchState,
  CentralNowCounts,
  CentralOverviewView,
} from '../components/central/CentralOverviewView';
import { CentralAgentCards } from '../components/central/CentralAgents';
import '../styles/mendsAi.css';
import '../styles/central.css';

type HubTab = 'insights' | 'funcionarios' | 'oportunidades' | 'aprovacoes';

function resolveTab(raw: string | null): HubTab {
  if (raw === 'aprovacoes' || raw === 'approvals') return 'aprovacoes';
  if (raw === 'oportunidades' || raw === 'opportunities') return 'oportunidades';
  if (raw === 'funcionarios') return 'funcionarios';
  return 'insights';
}

/**
 * Central de Inteligência (Bloco 2): analisa a operação e orienta os próximos passos. O imediato fica na
 * tela Hoje; aqui cada número tem "como é calculado", cada risco abre os casos reais e cada recomendação
 * diz o que a ação faz. Filtros (período e conexão) ficam na URL: voltar de uma conversa mantém a visão.
 */
const IntelligenceCenterPage: React.FC = () => {
  const { selectedSectorId, sectors } = useSector();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab = resolveTab(params.get('tab'));
  const period = parsePeriod(params.get('periodo'));
  const scope = params.get('conexao') || '';

  const sectorList = useMemo(() => sectors || [], [sectors]);
  const sectorIds = useMemo(() => {
    if (scope === 'todas') {
      const all = sectorList.map((s) => s.id).filter((id) => id > 0);
      return all.length > 0 ? all : (selectedSectorId ? [Number(selectedSectorId)] : []);
    }
    const fromUrl = Number(scope);
    if (fromUrl > 0 && sectorList.some((s) => s.id === fromUrl)) return [fromUrl];
    return selectedSectorId ? [Number(selectedSectorId)] : [];
  }, [scope, sectorList, selectedSectorId]);
  const idsKey = sectorIds.join(',');

  const [overview, setOverview] = useState<CentralOverview | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [now, setNow] = useState<CentralNowCounts | null>(null);
  const [nowError, setNowError] = useState<string | null>(null);
  const [analysis, setAnalysis] = useState<CentralAnalysis | null>(null);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [batches, setBatches] = useState<Record<string, BatchState>>({});
  const [plans, setPlans] = useState<Record<string, PlanState>>({});
  const [topics, setTopics] = useState<CentralTopics | null>(null);
  const [topicsLoading, setTopicsLoading] = useState(false);
  const [topicsError, setTopicsError] = useState<string | null>(null);
  const requestRef = useRef(0);

  const rawTab = params.get('tab');
  useEffect(() => {
    if (rawTab === 'hoje') navigate('/hoje', { replace: true });
  }, [rawTab, navigate]);

  const loadAnalysis = useCallback(async (ids: number[], refresh: boolean, req: number) => {
    setAnalysisLoading(true);
    try {
      const a = await fetchCentralAnalysis(ids, period, refresh);
      if (requestRef.current === req) setAnalysis(a);
    } catch (e: unknown) {
      if (requestRef.current === req) {
        setAnalysis({
          available: false,
          unavailableReason: apiErrorMessage(e, 'A leitura da IA falhou agora. Os números e casos continuam valendo.'),
          summary: '',
          priorities: [],
          limits: [],
          generatedAt: new Date().toISOString(),
        });
      }
    } finally {
      if (requestRef.current === req) setAnalysisLoading(false);
    }
  }, [period]);

  /** Cada chamada classifica um lote do que falta; repete enquanto houver pendentes (teto de rodadas). */
  const loadTopics = useCallback(async (ids: number[], req: number) => {
    setTopicsLoading(true);
    setTopicsError(null);
    try {
      for (let round = 0; round < 8; round += 1) {
        const t = await fetchCentralTopics(ids, period, true);
        if (requestRef.current !== req) return;
        setTopics(t);
        if (!t.available || t.pending === 0) break;
      }
    } catch (e: unknown) {
      if (requestRef.current === req) setTopicsError(apiErrorMessage(e, 'Não foi possível ler os assuntos agora.'));
    } finally {
      if (requestRef.current === req) setTopicsLoading(false);
    }
  }, [period]);

  const loadNow = useCallback(async (ids: number[], req: number) => {
    setNow(null);
    setNowError(null);
    // Mesmo endpoint e cache da tela Hoje (CI-04): os números batem com o que o Hoje mostra.
    const results = await Promise.allSettled(ids.map((id) => fetchTodayV2(id)));
    if (requestRef.current !== req) return;
    const ok = results.flatMap((r) => (r.status === 'fulfilled' ? [r.value] : []));
    if (ok.length === 0) {
      setNowError('Não foi possível ler a operação de agora. Abra a tela Hoje ou tente atualizar.');
      return;
    }
    setNow({
      aiAttending: ok.reduce((s, d) => s + (d.aiAttendingCount ?? 0), 0),
      awaitingReply: ok.reduce((s, d) => s + (d.awaitingReplyCount ?? 0), 0),
      needsHuman: ok.reduce((s, d) => s + (d.needsHumanCount ?? 0), 0),
      riskSilent: ok.reduce((s, d) => s + (d.riskSilentCount ?? 0), 0),
      approvalsPending: ok.reduce((s, d) => s + (d.approvalsPending ?? 0), 0),
      slaBreached: ok.reduce((s, d) => s + (d.slaBreachedCount ?? 0), 0),
    });
  }, []);

  const load = useCallback(async (refresh = false) => {
    if (sectorIds.length === 0) return;
    const req = ++requestRef.current;
    setLoading(true);
    setError(null);
    if (!refresh) {
      setAnalysis(null);
      setBatches({});
      setPlans({});
      setTopics(null);
    }
    void loadNow(sectorIds, req);
    void loadTopics(sectorIds, req);
    try {
      const o = await fetchCentralOverview(sectorIds, period, refresh);
      if (requestRef.current !== req) return;
      setOverview(o);
      void loadAnalysis(sectorIds, refresh, req);
    } catch (e: unknown) {
      if (requestRef.current !== req) return;
      setOverview(null);
      setError(apiErrorMessage(e, 'Não foi possível carregar a Central.'));
    } finally {
      if (requestRef.current === req) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsKey, period, loadAnalysis, loadNow, loadTopics]);

  useEffect(() => {
    if (tab === 'insights' || tab === 'funcionarios') void load(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, idsKey, period]);

  const setParam = (key: string, value: string | null) => {
    const next = new URLSearchParams(params);
    if (value === null || value === '') next.delete(key);
    else next.set(key, value);
    if (key === 'tab' && value !== 'aprovacoes') next.delete('id');
    setParams(next);
  };

  /** "Executar plano" já confirmado na prévia: servidor faz os internos e cria as aprovações (idempotente no dia). */
  const executePlan = async (rec: CentralRecommendation) => {
    setPlans((p) => ({ ...p, [rec.id]: { running: true } }));
    try {
      const run = await runCentralPlan(rec, sectorIds, period);
      setPlans((p) => ({ ...p, [rec.id]: { running: false, run } }));
    } catch (e: unknown) {
      setPlans((p) => ({ ...p, [rec.id]: { running: false, error: apiErrorMessage(e, 'Não foi possível executar o plano.') } }));
      return;
    }
    try {
      const o = await fetchCentralOverview(sectorIds, period, true);
      setOverview(o);
    } catch {
      /* o resultado por cliente já está na tela */
    }
  };

  const runRecommendation = async (rec: CentralRecommendation) => {
    if (rec.actionKind === 'open') {
      if (rec.path) navigate(rec.path);
      return;
    }
    if (rec.actionKind === 'follow_up_sweep') {
      if (!selectedSectorId) return;
      try {
        await proposeFollowUps(Number(selectedSectorId), 5, 'manual');
        setParam('tab', 'aprovacoes');
      } catch (e: unknown) {
        setError(apiErrorMessage(e, 'Falha ao propor follow-ups.'));
      }
      return;
    }
    setBatches((b) => ({ ...b, [rec.id]: { running: true, done: 0, total: rec.targets.length, results: [] } }));
    const results = await runFollowUpBatch(rec, (done, total) => {
      setBatches((b) => ({ ...b, [rec.id]: { ...(b[rec.id] || { results: [] }), running: true, done, total } as BatchState }));
    });
    setBatches((b) => ({ ...b, [rec.id]: { running: false, done: results.length, total: results.length, results } }));
    // Recarrega para o status "já pedido pela Central" refletir o que ficou gravado.
    try {
      const o = await fetchCentralOverview(sectorIds, period, true);
      setOverview(o);
    } catch {
      /* o resultado por cliente já está na tela */
    }
  };

  const multiSectorAvailable = sectorList.length > 1;
  const scopeValue = scope === 'todas' ? 'todas' : String(sectorIds[0] ?? '');

  return (
    <div className="mai-page mai-intel">
      <header className="mai-page-header mai-intel-head">
        <div>
          <h1 className="mai-title">Central de Inteligência</h1>
          <p className="mai-subtitle">
            O que está acontecendo na operação, por quê e o que pode ser feito. O imediato fica na tela Hoje.
          </p>
        </div>
        <div className="mai-ci-filters">
          <label className="mai-ci-filter">
            <span>Período</span>
            <select value={period} onChange={(e) => setParam('periodo', e.target.value === '7d' ? null : e.target.value)}>
              {CENTRAL_PERIODS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
            </select>
          </label>
          {multiSectorAvailable && (
            <label className="mai-ci-filter">
              <span>Conexão</span>
              <select value={scopeValue} onChange={(e) => setParam('conexao', e.target.value)}>
                {scopeValue === '' && <option value="">Escolha uma conexão</option>}
                <option value="todas">Todas as conexões</option>
                {sectorList.map((s) => <option key={s.id} value={String(s.id)}>{s.name}</option>)}
              </select>
            </label>
          )}
          <button
            type="button"
            className="mai-btn mai-btn-ghost"
            onClick={() => void load(true)}
            disabled={loading || sectorIds.length === 0}
          >
            {loading ? 'Atualizando…' : 'Atualizar'}
          </button>
          <button
            type="button"
            className="mai-btn mai-btn-ghost"
            disabled={sectorIds.length === 0}
            onClick={() => openCentralQuestion({
              sectorIds,
              period,
              label: [
                CENTRAL_PERIODS.find((p) => p.key === period)?.label,
                scope === 'todas'
                  ? 'todas as conexões'
                  : sectorList.find((x) => x.id === sectorIds[0])?.name,
              ].filter(Boolean).join(' · '),
            })}
          >
            Perguntar à MENDS
          </button>
        </div>
      </header>

      <div className="mai-hub-tabs" role="tablist" aria-label="Central de Inteligência">
        {([
          ['insights', 'Visão geral'],
          ['funcionarios', 'Funcionários digitais'],
          ['oportunidades', 'Funil / Oportunidades'],
          ['aprovacoes', 'Aprovações'],
        ] as Array<[HubTab, string]>).map(([key, label]) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={tab === key}
            className={tab === key ? 'mai-tab is-active' : 'mai-tab'}
            onClick={() => setParam('tab', key === 'insights' ? null : key)}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'oportunidades' && <OpportunitiesPage embedded />}
      {tab === 'aprovacoes' && <ApprovalsPage embedded />}
      {(tab === 'insights' || tab === 'funcionarios') && (
        <>
          {sectorIds.length === 0 && (
            <p className="mai-ci-empty">Selecione uma conexão no menu para carregar a Central.</p>
          )}
          {error && (
            <div className="mai-ci-error" role="alert">
              <p>{error}</p>
              <button type="button" className="mai-btn mai-btn-ghost" onClick={() => void load(true)}>Tentar de novo</button>
            </div>
          )}
          {loading && !overview && sectorIds.length > 0 && (
            <div className="mai-empty-state is-loading" aria-busy="true">
              <span className="mai-skeleton-bar" />
              <span className="mai-skeleton-bar is-short" />
              <span className="mai-visually-hidden">Calculando os números do período…</span>
            </div>
          )}
          {overview && tab === 'insights' && (
            <CentralOverviewView
              overview={overview}
              now={now}
              nowError={nowError}
              analysis={analysis}
              analysisLoading={analysisLoading}
              onRetryAnalysis={() => void loadAnalysis(sectorIds, true, requestRef.current)}
              batches={batches}
              onRunRecommendation={(r) => void runRecommendation(r)}
              plans={plans}
              onExecutePlan={(r) => void executePlan(r)}
              onOpen={(p) => navigate(p)}
              onShowAgents={() => setParam('tab', 'funcionarios')}
              topicsSlot={(
                <CentralTopicsPanel
                  topics={topics}
                  loading={topicsLoading}
                  error={topicsError}
                  onOpen={(p) => navigate(p)}
                  onRetry={() => void loadTopics(sectorIds, requestRef.current)}
                />
              )}
            />
          )}
          {overview && tab === 'funcionarios' && (
            <section className="mai-ci">
              <p className="mai-ci-muted">
                Saúde e carga de cada funcionário digital em {overview.period.label.toLowerCase()}. &quot;Sem resposta&quot; são
                atendimentos em que a IA recebeu a mensagem e não respondeu; a causa de cada um fica no registro de IA.
              </p>
              <CentralAgentCards
                agents={overview.agents}
                showSector={overview.sectors.length > 1}
                periodLabel={overview.period.label}
                onOpen={(p) => navigate(p)}
              />
            </section>
          )}
        </>
      )}
    </div>
  );
};

export default IntelligenceCenterPage;
